import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, X } from "lucide-react";

const STORAGE_KEY = "pw-academy-announcement-seen";

export default function AcademyPopup() {
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (window.sessionStorage.getItem(STORAGE_KEY)) return undefined;

    const timer = window.setTimeout(() => setOpen(true), 1200);
    return () => window.clearTimeout(timer);
  }, []);

  useEffect(() => {
    if (!open) return undefined;
    const onKeyDown = (event) => {
      if (event.key === "Escape") dismiss();
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [open]);

  const dismiss = () => {
    window.sessionStorage.setItem(STORAGE_KEY, "true");
    setOpen(false);
  };

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center bg-black/90 backdrop-blur-md p-4 md:p-8"
      role="dialog"
      aria-modal="true"
      aria-labelledby="academy-popup-title"
      data-testid="academy-popup"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) dismiss();
      }}
    >
      <div className="relative grid w-full max-w-4xl max-h-[92vh] overflow-y-auto bg-[#090909] border border-[#FF6B00]/50 shadow-[0_0_90px_rgba(255,107,0,0.18)] md:grid-cols-[0.9fr_1.1fr]">
        <button
          type="button"
          onClick={dismiss}
          data-testid="academy-popup-close"
          className="absolute right-3 top-3 z-10 grid h-10 w-10 place-items-center rounded-full bg-black/80 border border-white/20 text-white hover:border-[#FF6B00] hover:text-[#FF6B00] transition-colors"
          aria-label="Close Academy announcement"
        >
          <X size={20} />
        </button>

        <div className="relative min-h-[280px] md:min-h-[590px] overflow-hidden bg-black">
          <img
            src="/pickle-wall-academy-tryouts.png"
            alt="Pickle Wall Academy Central Florida team tryouts"
            className="absolute inset-0 h-full w-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/55 via-transparent to-transparent md:bg-gradient-to-r md:from-transparent md:to-black/30" />
        </div>

        <div className="relative flex flex-col justify-center px-7 py-9 md:px-11 md:py-12">
          <div className="text-xs font-black uppercase tracking-[0.28em] text-[#FF6B00]">
            A new era begins
          </div>
          <h2
            id="academy-popup-title"
            className="mt-4 font-display text-5xl md:text-6xl font-black uppercase leading-[0.88] tracking-tight"
          >
            Pickle Wall
            <span className="block text-[#FF6B00]">Academy</span>
          </h2>
          <p className="mt-6 text-lg text-white/75 leading-relaxed">
            Central Florida team tryouts are opening for athletes ages 10–18.
            Train with structure. Compete with purpose. Build your game every day.
          </p>

          <div className="mt-7 grid grid-cols-3 border-y border-white/10 py-5 text-center">
            <div>
              <div className="font-display text-2xl font-black text-[#FF6B00]">2×</div>
              <div className="mt-1 text-[10px] uppercase tracking-wider text-white/50">Team training</div>
            </div>
            <div className="border-x border-white/10">
              <div className="font-display text-2xl font-black text-[#FF6B00]">Daily</div>
              <div className="mt-1 text-[10px] uppercase tracking-wider text-white/50">Home system</div>
            </div>
            <div>
              <div className="font-display text-2xl font-black text-[#FF6B00]">$59</div>
              <div className="mt-1 text-[10px] uppercase tracking-wider text-white/50">Tryout</div>
            </div>
          </div>

          <Link
            to="/academy"
            onClick={dismiss}
            data-testid="academy-popup-explore-cta"
            className="mt-7 inline-flex items-center justify-center gap-2 bg-[#FF6B00] px-6 py-4 text-sm font-black uppercase tracking-[0.14em] text-black hover:bg-[#ff8129] transition-colors"
          >
            Explore the Academy <ArrowRight size={18} />
          </Link>
          <button
            type="button"
            onClick={dismiss}
            data-testid="academy-popup-dismiss"
            className="mt-3 py-2 text-xs uppercase tracking-widest text-white/40 hover:text-white/70"
          >
            Continue to Pickle Wall
          </button>
        </div>
      </div>
    </div>
  );
}
